<template>
  <span>
    <caption v-if="title">{{ title }}</caption>

    <HfTable
      v-if="!hasError"
      v-model:current-page="currentPage"
      :rows="shownRows"
      :columns="columns"
      narrowed
      striped
      hoverable
      :paginated="pagination"
      :per-page="paginationPageSize"
      :default-sort="defaultSort"
      :default-sort-direction="defaultSortDirection"
      :class="tableClass"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <template #header="{ column }">
        <!-- the span stays when the header is hidden, otherwise the table falls back to the plain label -->
        <span>
          <HfIcon
            v-if="showHeader && isSortedBy(column) && column.numeric"
            icon="arrow-up"
            size="is-small"
            class="hf-sort-icon is-numeric"
            :class="{'is-desc': sortOrder === 'desc'}"
          />
          <HfTooltip
            v-if="showHeader && column.headerTooltip"
            :label="column.headerTooltip"
            dashed
            :delay="500"
            class="is-info"
          >
            {{ column.label }}
          </HfTooltip>
          <template v-else-if="showHeader">
            {{ column.label }}
          </template>
          <HfIcon
            v-if="showHeader && isSortedBy(column) && !column.numeric"
            icon="arrow-up"
            size="is-small"
            class="hf-sort-icon"
            :class="{'is-desc': sortOrder === 'desc'}"
          />
        </span>
      </template>

      <template #cell="{ column, row }">
        <component
          :is="column.renderer"
          v-if="column.renderer"
          :props="{value: row[column.field], data: row}"
        />
        <HfTooltip
          v-else-if="valueTooltip"
          :label="valueTooltip(row)"
          position="is-left"
        >
          {{ format(row[column.field], column.valueFormatter) }}
        </HfTooltip>
        <template v-else>
          {{ format(row[column.field], column.valueFormatter) }}
        </template>
      </template>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p> {{ emptyMessage }}</p>
          </div>
        </section>
      </template>
    </HfTable>

    <div
      v-else
      class="has-text-centered"
    >
      <span class="is-error hf-text-muted">Fehler beim laden ... </span>
      <a @click="reload">
        <HfTooltip
          label="Neu laden"
          class="is-info"
          position="is-right"
        >
          <HfIcon
            icon="sync"
            size="is-small"
          />
        </HfTooltip>
      </a>
    </div>
  </span>
</template>

<script setup lang="ts">
  import {type Component, computed, onActivated, onDeactivated, ref, watch} from 'vue';
  import {STATS_SERVICE} from '@/services/stats.service';
  import COLUMNS from '@/services/columns/column.service';
  import {COLUMN_TYPES} from '@/services/columns/columns.base';
  import GridCountryFlag from '@/components/stats/renderer/GridCountryFlag.vue';
  import GridPlayerLink from '@/components/stats/renderer/GridPlayerLink.vue';
  import GridTeamLogo from '@/components/stats/renderer/GridTeamLogo.vue';
  import GridSeasonLink from '@/components/stats/renderer/GridSeasonLink.vue';
  import GridGameLink from '@/components/stats/renderer/GridGameLink.vue';
  import HfTable from '@/components/ui/table/HfTable.vue';
  import HfIcon from '@/components/ui/HfIcon.vue';
  import HfTooltip from '@/components/ui/HfTooltip.vue';
  import {Row, SortOrder, TableColumn} from '@/components/ui/table/table.model';
  import {StatsParams, StatsQueryParams, Threshold} from '@/model/stats/stats.model';
  import {useContextStore} from '@/store/context.store';
  import {log} from '@/utils/logger';

  const DEFAULT_PAGE_SIZE = 20;
  const MAX_PAGE_SIZE = 50;

  // column definitions name the renderer, see columns.base.ts
  const RENDERERS: Record<string, Component> = {GridCountryFlag, GridPlayerLink, GridTeamLogo, GridGameLink, GridSeasonLink};

  type FormatterParams = {value: unknown};
  type Formatter = (params: FormatterParams) => string;
  type CellClass = string | ((params: FormatterParams) => string);

  type StatsColumn = TableColumn & {
    headerTooltip?: string;
    renderer?: Component;
    valueFormatter?: Formatter;
  };

  const props = withDefaults(defineProps<{
    resource: string;
    query: string;
    fieldIds: string[];
    queryParams: StatsQueryParams;
    title?: string;
    pageSize?: number;
    suppressPaging?: boolean;
    suppressSorting?: boolean;
    showHeader?: boolean;
    defaultSort?: string;
    defaultSortDirection?: SortOrder;
    /** shown when hovering a value, i.e. a cell without a renderer */
    valueTooltip?: (row: Row) => string;
    /** rows it rejects are not shown */
    rowFilter?: (row: Row) => boolean;
    /** shown when the answer has rows, but the filter rejects all of them */
    filteredEmptyMessage?: string;
  }>(), {
    title: undefined,
    pageSize: undefined,
    suppressPaging: false,
    suppressSorting: false,
    showHeader: true,
    defaultSort: undefined,
    defaultSortDirection: 'desc',
    valueTooltip: undefined,
    rowFilter: undefined,
    filteredEmptyMessage: 'Keine Einträge',
  });

  const emit = defineEmits<{
    'hf-page-change': [page: number];
    'hf-threshold': [threshold: Threshold | null];
  }>();

  const context = useContextStore();

  const rowData = ref<Row[]>([]);
  const shownRows = computed(() => props.rowFilter ? rowData.value.filter(props.rowFilter) : rowData.value);
  const currentPage = ref(1);
  // the sort icon is rendered in the header slot, so the current sort is tracked here
  const sortField = ref(props.defaultSort);
  const sortOrder = ref<SortOrder>(props.defaultSortDirection);

  let isDeactivated = false;
  let isDirty = false;
  const isLoading = ref(false);
  const hasError = ref(false);

  function onPageChange(page: number) {
    emit('hf-page-change', page);
  }

  function onSort(field: string, order: SortOrder) {
    sortField.value = field;
    sortOrder.value = order;
  }

  function isSortedBy(column: TableColumn) {
    return !!column.sortable && column.field === sortField.value;
  }

  function reload() {
    loadData();
  }

  const emptyMessage = computed(() => {
    if (isLoading.value) {
      return 'Lädt ...';
    }
    return rowData.value.length > 0 ? props.filteredEmptyMessage : 'Keine Daten';
  });

  function format(value: unknown, formatter?: Formatter): string {
    if (formatter instanceof Function) {
      return formatter({value});
    }
    return value === undefined || value === null ? '' : String(value);
  }

  function cellClass(cellClass: CellClass | undefined, field: string): ((row: Row) => string | undefined) | undefined {
    if (cellClass instanceof Function) {
      return (row: Row) => cellClass({value: row[field]});
    }
    if (cellClass) {
      return () => cellClass;
    }
    return undefined;
  }

  const tableClass = computed(() => props.showHeader ? '' : 'hf-no-header');

  // the definitions (columns.base.ts) are untyped ag-grid era objects, hence the loose access here
  /* eslint-disable @typescript-eslint/no-explicit-any */
  function createColumnDefinitions(resource: string, fieldIds: string[]): any[] {
    const definitions = COLUMNS.definitions(resource, fieldIds);
    return fieldIds.map(fieldId => {
      const definition = definitions[fieldId];
      let field: any = {};
      if (definition) {
        field = {...definition, sortable: !definition.unsortable};
        delete field.unsortable;
      } else {
        field.headerName = `${fieldId.toUpperCase()}!`;
        field.sortable = false;
      }
      return {...field, field: fieldId};
    });
  }

  const columns = computed<StatsColumn[]>(() => {
    return createColumnDefinitions(props.resource, props.fieldIds).map((col: any) => {
      // resolve column-types, not overwriting
      const type = COLUMN_TYPES[col.type] || {};
      const rendererName: string | undefined = type.cellRendererFramework || col.cellRendererFramework;
      return {
        field: col.field,
        label: type.headerName || col.headerName || '',
        numeric: type.type === 'numericColumn',
        centered: type.centered || col.centered,
        sortable: !props.suppressSorting && !type.unsortable && col.sortable,
        headerTooltip: col.headerTooltip,
        cellClass: cellClass(type.cellClass || col.cellClass, col.field),
        valueFormatter: type.valueFormatter || col.valueFormatter,
        renderer: rendererName ? RENDERERS[rendererName] : undefined,
      };
    });
  });
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const params = computed<StatsParams>(() => {
    const result: StatsParams = {
      resource: props.resource,
      query: props.query,
      season: context.focusSeasonId,
      league: context.focusLeagueId,
      gameType: props.queryParams.gameType,
      team: props.queryParams.team,
      orderBy: props.queryParams.orderBy,
      ha: props.queryParams.ha,
      playerId: props.queryParams.playerId,
      limitRows: props.queryParams.limitRows,
    };
    if (context.focusTeamId) {
      result.team = context.focusTeamId;
    }
    return result;
  });

  function loadData() {
    if (isDeactivated) {
      return;
    }
    rowData.value = [];
    isLoading.value = true;
    hasError.value = false;
    STATS_SERVICE.stats(params.value).then(result => {
      rowData.value = result.rows;
      emit('hf-threshold', result.threshold);
    }).catch(() => {
      // the failed request is already reported by the http service
      hasError.value = true;
    }).finally(() => {
      isLoading.value = false;
    });
  }

  function onParamChange() {
    if (isDeactivated) {
      isDirty = true;
    } else {
      loadData();
    }
  }

  watch([
    () => context.focusLeagueId,
    () => context.focusSeasonId,
    () => context.focusGameTypeId,
    () => context.focusTeamId,
    () => props.queryParams.playerId,
    () => props.query,
  ], onParamChange);

  onActivated(() => {
    isDeactivated = false;
    if (isDirty) {
      loadData();
      isDirty = false;
    }
  });

  onDeactivated(() => {
    isDeactivated = true;
    isDirty = false;
  });

  const paginationPageSize = computed(() => {
    let pageSize;
    if (props.suppressPaging) {
      pageSize = shownRows.value.length;
    } else if (props.pageSize) {
      pageSize = props.pageSize;
    } else {
      pageSize = DEFAULT_PAGE_SIZE;
      log.warn(`No paginationPageSize and not supressing paging. Using default page-size of ${pageSize}`);
    }
    if (pageSize > MAX_PAGE_SIZE) {
      log.warn(`Page-size too big: propably wont fit into one screen-height. Should not exceed ${MAX_PAGE_SIZE}, but it's ${pageSize}`);
    }
    return pageSize;
  });

  const pagination = computed(() => !props.suppressPaging && !isNaN(paginationPageSize.value));

  function setPage(page: number) {
    currentPage.value = page;
  }

  defineExpose({setPage});

  loadData();
</script>

<style lang="scss">
  @import '../../assets/styles/variables';

  nav.pagination.is-simple {
    box-shadow: none;
  }

  td.hf-grid-club {
    padding: 0 2px 0 !important;

    span {

      img {
        max-width: 24px;
        width: 24px;
        vertical-align: text-top;
        opacity: $disabled-opacity;
      }
    }
  }

</style>
